import { Show, Suspense, createEffect, createSignal, lazy, onCleanup, onMount } from "solid-js";
import { Portal } from "solid-js/web";
import type { Me } from "./features/auth/api";
import { startLogin, startLogout, toggleAuth } from "./features/auth/api";
import { CalendarProvider } from "./features/calendar/store";
import { DateEventDialog } from "./features/date-event-dialog/DateEventDialog";
import { DateEventDialogProvider } from "./features/date-event-dialog/store";
import { loadViewTemplates } from "./features/calendar/view-templates";
import { VesselsProvider } from "./features/vessels/store";
import { VoyageDetailsModal } from "./features/voyage-details/VoyageDetailsModal";
import { TextTooltip } from "./shared/inputs/TextTooltip";
import { VoyageDetailsProvider } from "./features/voyage-details/store";
import { StatusBar } from "./features/status-bar/StatusBar";
import { startConnectionPolling, stopConnectionPolling } from "./features/status-bar/connection";
import { UpstreamProvider, useUpstream } from "./features/upstream-auth/store";
import { UpstreamMenu } from "./features/upstream-auth/UpstreamMenu";
import { ReauthModal } from "./features/upstream-auth/ReauthModal";
import { setHashState, setView, useHashState } from "./shared/routing/router";
import { useResponsiveTabs } from "./shared/responsive/useResponsiveTabs";
import s from "./App.module.css";

// Lazy-load each tab's view component so vis-timeline (~600 kB of the bundle)
// only ships once the calendar tab is actually visited. Combined with the
// first-visit gate below, this means a user who opens straight to /vessels
// never downloads vis-timeline.
const Calendar = lazy(() =>
  import("./features/calendar/Calendar").then((m) => ({ default: m.Calendar })),
);
const Vessels = lazy(() =>
  import("./features/vessels/Vessels").then((m) => ({ default: m.Vessels })),
);
const Settings = lazy(() =>
  import("./features/settings/Settings").then((m) => ({ default: m.Settings })),
);

export interface AppProps {
  me: Me;
}

export function App(props: AppProps) {
  // Lift `me` into a signal so the UserMenu can mutate auth state at
  // runtime (Enable/Disable) and the chrome reflects the new state
  // without a page reload.
  const [me, setMe] = createSignal<Me>(props.me);
  return (
    <VoyageDetailsProvider>
      {/* UpstreamProvider must wrap CalendarProvider so the calendar's
          retry-on-reauth effect can subscribe to the upstream auth
          state via useUpstream(). Without this, a getPorts failure
          during reauth-needed leaves portRefs empty and the user has
          to refresh manually after completing the auth flow. */}
      <UpstreamProvider>
        <CalendarProvider>
          <VesselsProvider>
            <DateEventDialogProvider>
              <AppInner me={me()} setMe={setMe} />
              <DateEventDialog />
            </DateEventDialogProvider>
          </VesselsProvider>
        </CalendarProvider>
      </UpstreamProvider>
    </VoyageDetailsProvider>
  );
}

function AppInner(props: { me: Me; setMe: (m: Me) => void }) {
  const hash = useHashState();
  const isView = (v: string) => hash().view === v;
  const upstream = useUpstream();
  const [modalOpen, setModalOpen] = createSignal(false);
  const onReauth = async () => {
    await upstream.startReauth();
    setModalOpen(true);
  };

  // Once a tab has been visited it stays mounted (so its transient component
  // state — open dropdowns, scroll position — survives a tab switch). The
  // store providers above this component own the long-lived data state.
  const [calendarVisited, setCalendarVisited] = createSignal(isView("calendar"));
  const [vesselsVisited, setVesselsVisited] = createSignal(isView("vessels"));
  const [settingsVisited, setSettingsVisited] = createSignal(isView("settings"));
  createEffect(() => {
    if (isView("calendar")) setCalendarVisited(true);
    if (isView("vessels")) setVesselsVisited(true);
    if (isView("settings")) setSettingsVisited(true);
  });

  // Backend reachability — long-lived WebSocket to /api/ws/ping powers
  // the connection indicator on the right side of the StatusBar.
  // 5 s ping cadence; missed pong (>4 s) or socket close flips the
  // indicator to red and triggers an exponential-backoff reconnect.
  onMount(() => startConnectionPolling());
  onCleanup(() => stopConnectionPolling());

  // Warm the view-templates cache once at boot so the PortSelector
  // toolbar's Load popover and the Settings → View templates list both
  // start with up-to-date contents.
  onMount(() => { void loadViewTemplates(); });

  // Responsive header collapse — measured (not media-queried) off the
  // tabs cell. See shared/responsive/useResponsiveTabs (DOM wiring) +
  // tab-collapse (the tested hysteresis decision). Also lifts the state
  // to <html data-compact> so any surface can react via compact.ts.
  const { tabsIconOnly, brandSealOnly, setTabsWrap, setTabsNav } = useResponsiveTabs();

  return (
    <div class={s.app}>
      <header class={s.brand}>
        <div ref={setTabsWrap} class={s.tabsWrap}>
        <nav
          ref={setTabsNav}
          class={s.tabs}
          role="tablist"
          data-icon-only={tabsIconOnly() ? "true" : "false"}
        >
          <TextTooltip text="Port Calendar" description="Gantt timeline of voyages per terminal">
            <a
              href="#/calendar"
              class={`${s.tab} ${isView("calendar") ? s.tabActive : ""}`}
              role="tab"
              aria-label="Port Calendar"
              onClick={(e) => {
                if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return;
                e.preventDefault();
                setView("calendar");
              }}
            >
              <span class={s.tabIcon} aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
                  {/* Calendar frame */}
                  <rect x="2" y="3.5" width="12" height="10.5" rx="1.3" stroke="currentColor" stroke-width="1.4" />
                  {/* Top header band */}
                  <line x1="2" y1="6.5" x2="14" y2="6.5" stroke="currentColor" stroke-width="1.4" />
                  {/* Binding pegs */}
                  <line x1="5" y1="2" x2="5" y2="4.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" />
                  <line x1="11" y1="2" x2="11" y2="4.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" />
                  {/* Gantt-style bars inside */}
                  <line x1="4" y1="9.5"  x2="9"  y2="9.5"  stroke="currentColor" stroke-width="1.4" stroke-linecap="round" />
                  <line x1="6" y1="11.7" x2="12" y2="11.7" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" />
                </svg>
              </span>
              <span class={s.tabLabel}>Port Calendar</span>
            </a>
          </TextTooltip>
          <TextTooltip text="Vessels & Voyages" description="Sortable, filterable table of all voyages">
            <a
              href="#/vessels"
              class={`${s.tab} ${isView("vessels") ? s.tabActive : ""}`}
              role="tab"
              aria-label="Vessels and Voyages"
              onClick={(e) => {
                if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return;
                e.preventDefault();
                setView("vessels");
              }}
            >
              <span class={s.tabIcon} aria-hidden="true">
                {/* Lucide "ship" silhouette — side view, well-known
                    pictogram, designed for icon-size legibility. */}
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  {/* Waves get a thicker stroke than the rest of the
                      icon so they read as a clear water line under the
                      hull rather than a wispy detail. */}
                  <path d="M2 21c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1s1.2 1 2.5 1c2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1" stroke-width="2.6" />
                  <path d="M19.38 20A11.6 11.6 0 0 0 21 14l-9-4-9 4c0 2.9.94 5.34 2.81 7.76" />
                  <path d="M19 13V7a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v6" />
                  <path d="M12 10v0" />
                  <path d="M12 5V2.5" />
                </svg>
              </span>
              <span class={s.tabLabel}>Vessels &amp; Voyages</span>
            </a>
          </TextTooltip>
        </nav>
        </div>
        <div class={s.brandWrap}>
        <div
          class={s.brandGroup}
          data-seal-only={brandSealOnly() ? "true" : "false"}
          aria-label="WW Operations Dashboard"
        >
          <svg
            class={s.brandSeal}
            viewBox="0 0 56 56"
            xmlns="http://www.w3.org/2000/svg"
            role="img"
            aria-label="WW"
          >
            <defs>
              <linearGradient id="brand-grad" x1="0" y1="0" x2="56" y2="56" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stop-color="#6366f1" />
                <stop offset="50%" stop-color="#8b5cf6" />
                <stop offset="100%" stop-color="#06b6d4" />
              </linearGradient>
            </defs>
            <rect x="2" y="2" width="52" height="52" rx="10" fill="url(#brand-grad)" />
            <rect x="6" y="6" width="44" height="44" rx="7" fill="none" stroke="#fff" stroke-width="1" opacity="0.35" />
            <g fill="none" stroke="#fff" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="28" cy="12.5" r="1.7" />
              <line x1="28" y1="14.2" x2="28" y2="29" />
              <line x1="24.5" y1="16.8" x2="31.5" y2="16.8" />
              <path d="M18 22 C 18 27.5, 22.5 29.5, 28 29.5 C 33.5 29.5, 38 27.5, 38 22" />
              <line x1="18" y1="22" x2="20" y2="23.2" />
              <line x1="38" y1="22" x2="36" y2="23.2" />
            </g>
            <text
              x="28"
              y="42"
              text-anchor="middle"
              font-family="Inter, system-ui, sans-serif"
              font-size="10"
              font-weight="800"
              letter-spacing="2"
              fill="#fff"
            >WW</text>
          </svg>
          <svg
            class={s.brandWordmark}
            viewBox="0 0 100 56"
            xmlns="http://www.w3.org/2000/svg"
            role="img"
            aria-label="Operations Dashboard"
          >
            <text
              x="0"
              y="27"
              font-family="Inter, system-ui, sans-serif"
              font-size="15"
              font-weight="700"
              letter-spacing="-0.2"
              fill="#0f172a"
            >Operations</text>
            <text
              x="0"
              y="44"
              font-family="'JetBrains Mono', ui-monospace, monospace"
              font-size="10"
              font-weight="500"
              letter-spacing="3"
              fill="#475569"
            >DASHBOARD</text>
          </svg>
        </div>
        </div>
        <div class={s.brandActions}>
          <SettingsMenu active={isView("settings")} currentSub={() => hash().params["s"]?.[0] ?? "colors"} />
          <UpstreamMenu
            state={upstream.state()}
            tokenExpiresAt={upstream.tokenExpiresAt()}
            cacheCount={upstream.cacheCount()}
            viewerEmail={props.me.email}
            onReauth={onReauth}
            onRefreshToken={() => void upstream.refreshToken()}
            onLogout={() => void upstream.logout()}
          />
          <UserMenu me={props.me} setMe={props.setMe} />
        </div>
      </header>
      <main class={s.main}>
        <div class={s.viewWrap} data-active={String(isView("calendar"))}>
          <Suspense fallback={<div class={s.lazyLoading}>Loading calendar…</div>}>
            {calendarVisited() ? <Calendar /> : null}
          </Suspense>
        </div>
        <div class={s.viewWrap} data-active={String(isView("vessels"))}>
          <Suspense fallback={<div class={s.lazyLoading}>Loading vessels…</div>}>
            {vesselsVisited() ? <Vessels /> : null}
          </Suspense>
        </div>
        <div class={s.viewWrap} data-active={String(isView("settings"))}>
          <Suspense fallback={<div class={s.lazyLoading}>Loading settings…</div>}>
            {settingsVisited() ? <Settings /> : null}
          </Suspense>
        </div>
      </main>
      <VoyageDetailsModal />
      <StatusBar />
      <ReauthModal
        open={modalOpen()}
        status={upstream.reauthStatus}
        streamFrames={upstream.streamFrames}
        onInput={upstream.sendInput}
        onClose={() => { upstream.endReauth(); setModalOpen(false); }}
      />
    </div>
  );
}

// Settings tab in the top nav — clicking the gear opens a small menu
// that navigates directly to a settings sub-section, skipping the
// intermediate sub-tab bar the Settings view used to render.
function SettingsMenu(props: { active: boolean; currentSub: () => string }) {
  const hash = useHashState();
  const [open, setOpen] = createSignal(false);
  let triggerEl: HTMLButtonElement | undefined;
  let menuEl: HTMLDivElement | undefined;
  const [menuPos, setMenuPos] = createSignal<{ top: number; right: number } | null>(null);

  function reposition() {
    if (!triggerEl) return;
    const r = triggerEl.getBoundingClientRect();
    setMenuPos({ top: r.bottom + 6, right: window.innerWidth - r.right });
  }
  function toggle() {
    if (open()) { setOpen(false); return; }
    reposition();
    setOpen(true);
  }
  // Blur the trigger on every close path (Escape, click-outside, menu
  // item) so the pill doesn't keep a focus ring after the menu hides.
  function close() { setOpen(false); triggerEl?.blur(); }

  function onDocDown(e: Event) {
    if (!open()) return;
    const t = e.target as Node;
    if (triggerEl?.contains(t)) return;
    if (menuEl?.contains(t)) return;
    close();
  }
  function onKey(e: KeyboardEvent) {
    if (e.key === "Escape" && open()) close();
  }
  function onWinChange() { if (open()) reposition(); }

  // Capture phase + pointerdown for parity with the rest of the popups —
  // vis-timeline's Hammer.js can swallow the bubble-phase mousedown.
  document.addEventListener("pointerdown", onDocDown, true);
  document.addEventListener("mousedown", onDocDown, true);
  document.addEventListener("keydown", onKey);
  window.addEventListener("resize", onWinChange);
  window.addEventListener("scroll", onWinChange, true);
  onCleanup(() => {
    document.removeEventListener("pointerdown", onDocDown, true);
    document.removeEventListener("mousedown", onDocDown, true);
    document.removeEventListener("keydown", onKey);
    window.removeEventListener("resize", onWinChange);
    window.removeEventListener("scroll", onWinChange, true);
  });

  function go(sub: string) {
    // Preserve other tabs' params (c.* / v.*) so going Calendar → Settings
    // → Calendar keeps the user's selected terminals + viewport intact.
    setHashState({ view: "settings", params: { ...hash().params, s: [sub] } });
    close();
    triggerEl?.blur();
  }

  return (
    <>
      <TextTooltip
        text="Settings"
        description="Terminal colours, date events, and view templates"
        disabled={open()}
      >
        <button
          ref={(el) => (triggerEl = el)}
          type="button"
          class={`${s.userPill} ${props.active || open() ? s.userPillOpen : ""}`}
          aria-haspopup="menu"
          aria-expanded={open()}
          aria-label="Settings"
          onClick={toggle}
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" class={s.userIcon}>
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
        </button>
      </TextTooltip>
      <Show when={open() && menuPos()}>
        {(pos) => (
          <Portal>
            <div
              ref={(el) => (menuEl = el)}
              class={s.tabMenu}
              role="menu"
              style={{ top: `${pos().top}px`, right: `${pos().right}px` }}
            >
              <button
                type="button"
                role="menuitem"
                class={`${s.tabMenuItem} ${props.active && props.currentSub() === "colors" ? s.tabMenuItemActive : ""}`}
                onClick={() => go("colors")}
              >
                Terminal colours
              </button>
              <button
                type="button"
                role="menuitem"
                class={`${s.tabMenuItem} ${props.active && props.currentSub() === "events" ? s.tabMenuItemActive : ""}`}
                onClick={() => go("events")}
              >
                Date events
              </button>
              <button
                type="button"
                role="menuitem"
                class={`${s.tabMenuItem} ${props.active && props.currentSub() === "templates" ? s.tabMenuItemActive : ""}`}
                onClick={() => go("templates")}
              >
                View templates
              </button>
            </div>
          </Portal>
        )}
      </Show>
    </>
  );
}

// Top-right user pill — clicking opens a small menu (Sign out, room
// for more later). The menu is Portal-rendered so it can't be clipped
// by the tabs row's border-bottom or any other overflow context.
function UserMenu(props: { me: Me; setMe: (m: Me) => void }) {
  const [open, setOpen] = createSignal(false);
  let triggerEl: HTMLButtonElement | undefined;
  let menuEl: HTMLDivElement | undefined;
  const [menuPos, setMenuPos] = createSignal<{ top: number; right: number } | null>(null);

  function reposition() {
    if (!triggerEl) return;
    const r = triggerEl.getBoundingClientRect();
    setMenuPos({ top: r.bottom + 6, right: window.innerWidth - r.right });
  }
  function toggle() {
    if (open()) { setOpen(false); return; }
    reposition();
    setOpen(true);
  }
  // Blur the trigger on every close path (Escape, click-outside) so
  // the pill doesn't keep a focus ring after the menu hides.
  function close() { setOpen(false); triggerEl?.blur(); }

  function onDocClick(e: MouseEvent) {
    if (!open()) return;
    const t = e.target as Node;
    if (triggerEl?.contains(t)) return;
    if (menuEl?.contains(t)) return;
    close();
  }
  function onKey(e: KeyboardEvent) {
    if (e.key === "Escape" && open()) close();
  }
  function onWinChange() { if (open()) reposition(); }

  // Capture phase + pointerdown + mousedown, matching SettingsMenu.
  // vis-timeline's Hammer.js can swallow bubble-phase mousedown on
  // anything overlapping the timeline, which leaves the menu stuck
  // open when the user clicks back onto the chart.
  document.addEventListener("pointerdown", onDocClick, true);
  document.addEventListener("mousedown", onDocClick, true);
  document.addEventListener("keydown", onKey);
  window.addEventListener("resize", onWinChange);
  window.addEventListener("scroll", onWinChange, true);
  onCleanup(() => {
    document.removeEventListener("pointerdown", onDocClick, true);
    document.removeEventListener("mousedown", onDocClick, true);
    document.removeEventListener("keydown", onKey);
    window.removeEventListener("resize", onWinChange);
    window.removeEventListener("scroll", onWinChange, true);
  });

  const identityLabel = () => props.me.authDisabled ? "Anonymous" : props.me.email;
  const aggBadge = () => props.me.authDisabled ? s.badgeError : s.badgeOk;

  return (
    <>
      <TextTooltip
        text="Local Authentication"
        stripe={props.me.authDisabled ? "#ef4444" : "#10b981"}
        disabled={open()}
        properties={
          props.me.authDisabled
            ? [
                { label: "Identity", value: "Anonymous" },
                { label: "Authentication", value: "Disabled" },
              ]
            : [
                { label: "Identity", value: props.me.email },
                { label: "Authentication", value: "Enabled" },
              ]
        }
      >
        <span class={s.pillWrap}>
          <button
            ref={(el) => (triggerEl = el)}
            type="button"
            class={`${s.userPill} ${open() ? s.userPillOpen : ""}`}
            aria-haspopup="menu"
            aria-expanded={open()}
            aria-label={props.me.authDisabled ? "User menu (auth disabled)" : `User menu — signed in as ${props.me.email}`}
            onClick={toggle}
          >
            <svg class={s.userIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </button>
          <span class={`${s.pillBadge} ${aggBadge()}`} aria-hidden="true" />
        </span>
      </TextTooltip>
      <Show when={open() && menuPos()}>
        {(pos) => (
          <Portal>
            <div
              ref={(el) => (menuEl = el)}
              class={s.userMenu}
              role="menu"
              style={{ top: `${pos().top}px`, right: `${pos().right}px` }}
            >
              <header class={s.userMenuHeader}>
                <div class={s.userMenuEmail}>Local Authentication</div>
              </header>
              <div class={s.menuGrid}>
                <div class={s.menuHeaderCell}>Status</div>
                <div class={s.menuHeaderCell}>Kind</div>
                <div class={s.menuHeaderCell}>Detail</div>
                <div class={s.menuHeaderCell}>Action</div>

                <span class={`${s.menuCellDot} ${s.badgeOk}`} aria-hidden="true" />
                <span class={s.menuCellKind}>Identity</span>
                <span class={s.menuCellValue}>{identityLabel()}</span>
                <TextTooltip
                  text={props.me.authDisabled ? "Logout unavailable" : "Logout"}
                  description={props.me.authDisabled
                    ? "No session to log out from — auth is disabled on this backend"
                    : "Sign out of this dashboard"}
                  stripe={props.me.authDisabled ? undefined : "#ef4444"}
                >
                <button
                  type="button"
                  role="menuitem"
                  class={`${s.menuCellAction} ${s.menuCellActionDanger}`}
                  disabled={props.me.authDisabled}
                  onClick={() => { close(); void startLogout(); }}
                >
                  Logout
                </button>
                </TextTooltip>

                <span class={`${s.menuCellDot} ${props.me.authDisabled ? s.badgeError : s.badgeOk}`} aria-hidden="true" />
                <span class={s.menuCellKind}>Authentication</span>
                <span class={s.menuCellValue}>{props.me.authDisabled ? "Disabled" : "Enabled"}</span>
                <TextTooltip
                  text={props.me.authDisabled ? "Enable auth" : "Disable auth"}
                  description={
                    props.me.authDisabled
                      ? (props.me.canEnableAuth
                          ? "Turn on Google sign-in — you'll be redirected to authenticate"
                          : "OAuth env vars are not configured on this backend — cannot enable")
                      : "Disable Google sign-in. The dashboard becomes open to anyone who can reach the backend"
                  }
                  stripe={!props.me.authDisabled ? "#ef4444" : undefined}
                >
                <button
                  type="button"
                  role="menuitem"
                  class={`${s.menuCellAction} ${!props.me.authDisabled ? s.menuCellActionDanger : ""}`}
                  disabled={props.me.authDisabled && !props.me.canEnableAuth}
                  onClick={async () => {
                    close();
                    const action: "enable" | "disable" = props.me.authDisabled ? "enable" : "disable";
                    const r = await toggleAuth(action);
                    if ("error" in r) {
                      console.error(`[auth] toggle failed: ${r.error}`);
                      return;
                    }
                    if (action === "enable") {
                      // Auth is now required and our request was made
                      // pre-toggle (open). Bounce through Google so we
                      // come back with a session cookie.
                      startLogin();
                      return;
                    }
                    props.setMe({ ...props.me, authDisabled: r.authDisabled, canEnableAuth: r.canEnableAuth, email: "guest" });
                  }}
                >
                  {props.me.authDisabled ? "Enable" : "Disable"}
                </button>
                </TextTooltip>
              </div>
            </div>
          </Portal>
        )}
      </Show>
    </>
  );
}
